import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { MemberSupportChat } from "@/components/support/MemberSupportChat";
import {
  Clock,
  CreditCard,
  Heart,
  LifeBuoy,
  Mail,
  MessageCircle,
  ShieldAlert,
  Users,
} from "lucide-react";

export const Route = createFileRoute("/member-support")({
  head: () => ({
    meta: [
      { title: "Member Support — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Member support for Recovery Trauma™: answers to common membership and account questions, live chat with the support assistant, and a direct line to Jana and the team.",
      },
      { property: "og:title", content: "Member Support — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Live chat, member FAQs, and a direct way to reach Jana and the Recovery Trauma™ team — no email needed.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/member-support" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/member-support" }],
  }),
  component: MemberSupportPage,
});

type Faq = { q: string; a: string };

const FAQ_GROUPS: { id: string; title: string; icon: typeof CreditCard; items: Faq[] }[] = [
  {
    id: "membership",
    title: "Membership & billing",
    icon: CreditCard,
    items: [
      {
        q: "How do I change or cancel my membership?",
        a: "Open your Billing page from your account menu. You can switch plan or cancel there at any time, and you'll keep access until the end of the period you've already paid for. Nothing is hidden behind a phone call.",
      },
      {
        q: "Do I lose my journals and progress if I cancel?",
        a: "No. Your journals, check-ins and journey progress stay safely in your account. If you come back later, everything is waiting exactly where you left it.",
      },
      {
        q: "Where do I enter a discount or comeback code?",
        a: "At checkout, and on the restart card if you're returning, there's a 'Have a voucher code?' box. Enter the code there and the new price is shown before you pay anything.",
      },
      {
        q: "Where can I see my payment history?",
        a: "Your Payment history page lists every payment on your account, including the date and amount, so you can check anything at a glance.",
      },
    ],
  },
  {
    id: "account",
    title: "Account & access",
    icon: ShieldAlert,
    items: [
      {
        q: "I can't log in — what should I do?",
        a: "Use 'Forgot password' on the login screen to get a secure reset link; it lasts one hour. If the email doesn't arrive, check your spam folder, then start a live chat below and we'll look into it with you.",
      },
      {
        q: "Can I use Recovery Trauma™ on my phone and laptop?",
        a: "Yes. Your membership works on any device you sign in on, and everything stays in sync.",
      },
      {
        q: "How do I change which emails I receive?",
        a: "Your email preferences page lets you choose what you hear about, or turn emails off completely. Account and billing notices are the only things we still need to send.",
      },
    ],
  },
  {
    id: "using",
    title: "Using your membership",
    icon: Heart,
    items: [
      {
        q: "Where do I find the 30-day healing journeys?",
        a: "They're on your dashboard, on a day-by-day timeline — Breaking Free, Secure Within and Reset & Regulate. Tap the day you're up to and that day's practice opens straight away.",
      },
      {
        q: "Do I have to do a journey in order, or every day?",
        a: "Not at all. There's no streak to break and nothing to catch up on. Take the days at whatever pace feels right, and repeat any day as often as you like.",
      },
      {
        q: "Is my journal private?",
        a: "Yes. What you write is yours. We never read or analyse your entries, and any reflection feature is something you choose to switch on yourself.",
      },
    ],
  },
  {
    id: "community",
    title: "Community & safety",
    icon: Users,
    items: [
      {
        q: "How is the community kept safe?",
        a: "Every post sits under our trauma-informed community guidelines, you can report or block anyone, and reports come straight to us for review.",
      },
      {
        q: "Is Recovery Trauma™ therapy?",
        a: "No. Recovery Trauma™ is education and wellbeing support built from lived experience. It sits alongside professional care rather than replacing it, and it isn't a crisis service.",
      },
    ],
  },
];

function MemberSupportPage() {
  return (
    <div className="min-h-dvh bg-gradient-to-b from-background via-background to-[var(--gold-soft)]/25 pb-20 pt-8">
      <div className="mx-auto max-w-3xl space-y-8 px-4 sm:px-6">
        <header className="text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
            <LifeBuoy className="h-3.5 w-3.5" /> Member support
          </div>
          <h1 className="font-serif text-3xl tracking-tight text-foreground sm:text-4xl">
            We're here, and you don't need to write an email
          </h1>
          <p className="mx-auto mt-3 max-w-xl text-base leading-relaxed text-muted-foreground">
            Start a live chat, look through the questions members ask most, or send a note straight
            to Jana and the team. Take whichever feels easiest today.
          </p>
        </header>

        {/* Crisis first — never buried at the bottom */}
        <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-5">
          <div className="flex items-start gap-3">
            <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
            <div className="text-sm leading-relaxed text-foreground/85">
              <p className="font-semibold text-foreground">If you need urgent help</p>
              <p className="mt-1">
                Recovery Trauma™ is not a crisis service and cannot provide emergency support. If
                you are in immediate danger, please contact your local emergency services. Our
                crisis page lists helplines you can reach today.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Link
                  to="/crisis"
                  className="inline-flex min-h-10 items-center gap-1.5 rounded-full bg-destructive px-4 text-xs font-semibold text-destructive-foreground"
                >
                  <LifeBuoy className="h-3.5 w-3.5" /> Crisis resources
                </Link>
                <Link
                  to="/grounding"
                  className="inline-flex min-h-10 items-center rounded-full border border-border bg-background px-4 text-xs font-semibold text-foreground"
                >
                  Grounding tools
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Live chat */}
        <section aria-labelledby="live-chat-heading">
          <h2 id="live-chat-heading" className="sr-only">
            Live chat
          </h2>
          <MemberSupportChat />
        </section>

        {/* FAQs */}
        <section aria-labelledby="faq-heading" className="space-y-4">
          <div className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4 text-[var(--gold-deep)]" />
            <h2 id="faq-heading" className="font-serif text-2xl text-foreground">
              Questions members ask most
            </h2>
          </div>
          <div className="space-y-4">
            {FAQ_GROUPS.map((group) => {
              const Icon = group.icon;
              return (
                <div
                  key={group.id}
                  className="rounded-2xl border border-border bg-card p-4 sm:p-5"
                >
                  <div className="mb-2 flex items-center gap-2">
                    <Icon className="h-4 w-4 text-[var(--gold-deep)]" />
                    <h3 className="text-sm font-semibold text-foreground">{group.title}</h3>
                  </div>
                  <Accordion type="single" collapsible className="w-full">
                    {group.items.map((item, i) => (
                      <AccordionItem key={item.q} value={`${group.id}-${i}`}>
                        <AccordionTrigger className="text-left text-sm">{item.q}</AccordionTrigger>
                        <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                          {item.a}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              );
            })}
          </div>
          <p className="text-sm text-muted-foreground">
            Still looking?{" "}
            <Link to="/help" className="text-[var(--gold-deep)] underline-offset-2 hover:underline">
              Browse the full help centre
            </Link>{" "}
            or{" "}
            <Link to="/faq" className="text-[var(--gold-deep)] underline-offset-2 hover:underline">
              read the general FAQ
            </Link>
            .
          </p>
        </section>

        {/* How to reach Jana */}
        <section
          aria-labelledby="contact-heading"
          className="rounded-3xl border border-border bg-card p-5 sm:p-6"
        >
          <h2 id="contact-heading" className="font-serif text-2xl text-foreground">
            Reaching me directly
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            Recovery Trauma™ is survivor-led and small. When you write in, it's read by me or
            someone on my team — not a call centre. You're welcome to ask about anything, including
            the things that feel awkward to ask.
          </p>
          <ul className="mt-4 space-y-3 text-sm text-foreground/90">
            <li className="flex items-start gap-3">
              <MessageCircle className="mt-0.5 h-4 w-4 shrink-0 text-[var(--gold-deep)]" />
              <span>
                <span className="font-medium">Live chat above</span> — fastest for account,
                membership and how-to questions. Anything it can't answer goes to us with your chat
                attached, so you never repeat yourself.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <Mail className="mt-0.5 h-4 w-4 shrink-0 text-[var(--gold-deep)]" />
              <span>
                <span className="font-medium">Message form</span> — the{" "}
                <Link
                  to="/support"
                  className="text-[var(--gold-deep)] underline-offset-2 hover:underline"
                >
                  contact page
                </Link>{" "}
                if you'd rather write it all out in one go.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <Users className="mt-0.5 h-4 w-4 shrink-0 text-[var(--gold-deep)]" />
              <span>
                <span className="font-medium">Community</span> — for shared experience rather than
                account help, the{" "}
                <Link
                  to="/community"
                  className="text-[var(--gold-deep)] underline-offset-2 hover:underline"
                >
                  member community
                </Link>{" "}
                is there any hour of the day.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <Clock className="mt-0.5 h-4 w-4 shrink-0 text-[var(--gold-deep)]" />
              <span>
                <span className="font-medium">Response times</span> — usually within 1–2 business
                days, UK time. Billing questions are always prioritised.
              </span>
            </li>
          </ul>
          <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
            Please don't share detailed medical information with support. We can help with your
            membership and the platform, not with clinical care.
          </p>
        </section>
      </div>
    </div>
  );
}
